import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPlug,
  faWandMagicSparkles,
  faShareNodes,
  faComments,
  faChartLine,
  faShieldHalved,
  faBolt,
  faCode,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import InfoTooltip from './InfoTooltip.js';
import '../translations.js';
import './FirstBotPrompt.css';

// Real, connectable integrations — every logo maps to a shipped capability:
// deploy targets (Wix / Webflow / WordPress), knowledge connectors
// (Google Drive / OneDrive / Notion), Meta channels (Facebook / Instagram / WhatsApp)
// and the app skills surfaced on the Apps page (Calendly, Cal.com, HubSpot,
// Stripe, Mailchimp, Zendesk — see PROVIDER_META). Assets live in /public.
// `info` is shown on hover and describes what the agent can do with it.
const INTEGRATION_LOGOS = [
  { src: '/wix-logo.png', alt: 'Wix', infoKey: 'fbp_logo_wix' },
  { src: '/webflow-logo.png', alt: 'Webflow', infoKey: 'fbp_logo_webflow' },
  { src: '/wordpress-logo.png', alt: 'WordPress', infoKey: 'fbp_logo_wordpress' },
  { src: '/google-drive.png', alt: 'Google Drive', infoKey: 'fbp_logo_google_drive' },
  { src: '/onedrive.png', alt: 'OneDrive', infoKey: 'fbp_logo_onedrive' },
  { src: '/notion-logo.png', alt: 'Notion', infoKey: 'fbp_logo_notion' },
  { src: '/hubspot-logo.png', alt: 'HubSpot', infoKey: 'fbp_logo_hubspot' },
  { src: '/stripe-logo.png', alt: 'Stripe', infoKey: 'fbp_logo_stripe' },
  { src: '/mailchimp-logo.png', alt: 'Mailchimp', infoKey: 'fbp_logo_mailchimp' },
  { src: '/zendesk-logo.png', alt: 'Zendesk', infoKey: 'fbp_logo_zendesk' },
  { src: '/calendly-logo.png', alt: 'Calendly', infoKey: 'fbp_logo_calendly' },
  { src: '/calcom-logo.png', alt: 'Cal.com', infoKey: 'fbp_logo_calcom' },
  { src: '/facebook-logo.webp', alt: 'Facebook', infoKey: 'fbp_logo_facebook' },
  { src: '/instagram-logo.webp', alt: 'Instagram', infoKey: 'fbp_logo_instagram' },
  { src: '/whatsapp-logo.webp', alt: 'WhatsApp', infoKey: 'channels_whatsapp_desc' },
];

// Highlights lead with the platform's differentiators: deep integrations,
// connectivity and agentic skills that take real action on complex tasks.
const FEATURES = [
  {
    icon: faPlug,
    titleKey: 'fbp_feature_integrations_title',
    descKey: 'fbp_feature_integrations_desc',
  },
  {
    icon: faWandMagicSparkles,
    titleKey: 'fbp_feature_skills_title',
    descKey: 'fbp_feature_skills_desc',
  },
  {
    icon: faShareNodes,
    titleKey: 'fbp_feature_omnichannel_title',
    descKey: 'fbp_feature_omnichannel_desc',
  },
  {
    icon: faComments,
    titleKey: 'fbp_feature_conversations_title',
    descKey: 'fbp_feature_conversations_desc',
  },
  {
    icon: faChartLine,
    titleKey: 'fbp_feature_lead_capture_title',
    descKey: 'fbp_feature_lead_capture_desc',
  },
  {
    icon: faShieldHalved,
    titleKey: 'fbp_feature_safe_title',
    descKey: 'fbp_feature_safe_desc',
  },
];

function Feature({ icon, title, description }) {
  return (
    <div className="fbp-feature">
      <div className="fbp-feature-icon"><FontAwesomeIcon icon={icon} /></div>
      <div className="fbp-feature-body">
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
    </div>
  );
}

// ctaLabel: the button text where the page is the start of building (AI Studio: "Build a new agent")
function FirstBotPrompt({ onAddBot, ctaLabel = null }) {
  const { t } = useTranslation();

  return (
    <div className="firstBotPromptContainer">
      <div className="fbp-hero">
        <div className="fbp-hero-overlay" />
        {/* Integration logos pinned across the top of the hero. Hover a logo
            for what the agent can do with it. */}
        <div className="fbp-hero-logos">
          {INTEGRATION_LOGOS.map((logo) => (
            <InfoTooltip key={logo.src} text={t(logo.infoKey)} placement="bottom">
              <span className="fbp-logo">
                <img src={logo.src} alt={logo.alt} loading="lazy" />
              </span>
            </InfoTooltip>
          ))}
          <InfoTooltip text={t('fbp_any_api_tooltip')} placement="bottom">
            <span className="fbp-logo fbp-logo-api">
              <FontAwesomeIcon icon={faCode} /> {t('fbp_any_api_label')}
            </span>
          </InfoTooltip>
          <InfoTooltip text={t('fbp_more_tooltip')} placement="bottom">
            <span className="fbp-logo fbp-logo-more">{t('fbp_more_label')}</span>
          </InfoTooltip>
        </div>
        <div className="fbp-hero-inner">
          <h1 className="fbp-hero-title">{t('create_your_first_bot')}</h1>
          <p className="fbp-hero-sub">
            {t('fbp_hero_subtitle')}
          </p>
          <button className="firstBotPromptAddBtn" onClick={onAddBot} aria-label={t('fbp_add_agent_aria')}>
            <FontAwesomeIcon icon={faPlus} /> {ctaLabel || t('add_bot')}
          </button>
        </div>
      </div>

      <div className="fbp-sections">
        <section className="fbp-section fbp-section-features">
          <h3 className="fbp-section-title">
            <FontAwesomeIcon icon={faBolt} className="fbp-inline-icon" /> {t('fbp_platform_highlights')}
          </h3>
          <div className="fbp-feature-grid">
            {FEATURES.map((f) => (
              <Feature key={f.titleKey} icon={f.icon} title={t(f.titleKey)} description={t(f.descKey)} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default FirstBotPrompt;
